import { graphql, useFragment } from 'react-relay';
import React, { useCallback, forwardRef } from 'react';
import type { FC, MouseEvent } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import { styled } from '@compiled/react';

import type { TriggerProps } from '@atlaskit/popup/types';
import { token } from '@atlaskit/tokens';
import IconButton, { type IconButtonProps } from '@atlaskit/button/icon/button';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import EditorAddIcon from '@atlaskit/icon/core/add';

import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { canPerformSpaceOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';

import { useSpaceCreationEligibility } from '../../SpaceCreationEligibility.tsx';

import type {
	ContextualCreateContentButtonQuery$data,
	ContextualCreateContentButtonQuery$variables,
} from './__generated__/ContextualCreateContentButtonQuery.graphql.ts';
import type { ContextualCreateContentButton_space$key } from './__generated__/ContextualCreateContentButton_space.graphql.ts';

const contextualCreateContentButtonFragment = graphql`
	fragment ContextualCreateContentButton_space on Space {
		id
		operations {
			operation
			targetType
		}
	}
`;

const i18n = defineMessages({
	simpleCreateLabel: {
		id: 'create-blank-fabric-page.contextual-create.button.simple-create-label',
		description: 'Label for a button that creates content in the current space',
		defaultMessage: 'Create',
	},
	simpleCreateLabelForTitle: {
		id: 'create-blank-fabric-page.contextual-create.button.simple-create-label-for-title',
		description: 'Aria label for a button that creates content in the current space',
		defaultMessage: 'Create child content for {pageTitle}',
	},
});

const canCreateContent = (data: ContextualCreateContentButtonQuery$data) => {
	return canPerformSpaceOperation({
		operationCheckResult: data,
		operation: OperationTypes.CREATE,
		contentType: 'page',
	});
};

export const contextualCreateContentIconSVGColor = (isOpen?: boolean): string => {
	return isOpen ? token('color.icon.selected') : token('color.icon');
};

type ContextualCreateIconButtonProps = {
	isRootCreateButton: boolean;
	isOpen?: boolean;
};

// Prevent Compiled from passing down the isRootCreateButton and isOpen props to the DOM element and causing a React warning
const BaseIconButton = forwardRef<
	HTMLButtonElement,
	IconButtonProps & ContextualCreateIconButtonProps
>(({ isRootCreateButton: _isRootCreateButton, isOpen: _isOpen, ...props }, ref) => {
	return <IconButton {...props} ref={ref} />;
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-unsafe-style-overrides -- To migrate as part of go/ui-styling-standard
const ContextualCreateButton = styled(BaseIconButton)<ContextualCreateIconButtonProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&& svg': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		color: (props) => contextualCreateContentIconSVGColor(props.isOpen),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&:hover svg': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		color: (props) => (props.isRootCreateButton ? token('color.icon') : 'inherit'),
	},
});

type ContextualCreateButtonProps = {
	triggerProps: TriggerProps;
	onClick: (event: MouseEvent<HTMLElement>) => void;
	isContextualCreateFocused: boolean | undefined;
	isOpen?: boolean;
	spaceKey: string;
	rootCreateButtonStyling: boolean;
	displayTitle?: string;
};

export const ContextualCreateContentButton: FC<ContextualCreateButtonProps> = (props) => {
	const space = useSpaceCreationEligibility(props.spaceKey);
	return space ? (
		<FragmentContextualCreateContentButton {...props} spaceFragmentRef={space} />
	) : (
		<LegacyContextualCreateContentButton {...props} />
	);
};

const FragmentContextualCreateContentButton = ({
	spaceFragmentRef,
	...props
}: ContextualCreateButtonProps & { spaceFragmentRef: ContextualCreateContentButton_space$key }) => {
	const space = useFragment(contextualCreateContentButtonFragment, spaceFragmentRef);
	return <ContextualCreateContentButtonContent {...props} data={{ space }} loading={false} />;
};

const LegacyContextualCreateContentButton = (props: ContextualCreateButtonProps) => {
	const { spaceKey } = props;
	const { data, loading, error } = useQueryNoFurtherUpdateInterop<
		ContextualCreateContentButtonQuery$data,
		ContextualCreateContentButtonQuery$variables
	>(
		graphql`
			query ContextualCreateContentButtonQuery($spaceKey: String!) {
				space(key: $spaceKey) {
					...ContextualCreateContentButton_space @relay(mask: false)
				}
			}
		`,
		{ variables: { spaceKey } },
	);
	return (
		<ContextualCreateContentButtonContent {...props} data={data} loading={loading} error={error} />
	);
};

const ContextualCreateContentButtonContent: FC<
	ContextualCreateButtonProps & {
		data?: ContextualCreateContentButtonQuery$data | null;
		loading: boolean;
		error?: Error;
	}
> = ({
	data,
	loading,
	error,
	triggerProps,
	onClick,
	isContextualCreateFocused,
	isOpen,
	rootCreateButtonStyling,
	displayTitle,
}) => {
	const intl = useIntl();

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			onClick(e);
		},
		[onClick],
	);

	if (error && isOpen) {
		return <ErrorDisplay error={error} />;
	}

	const canCreate = data && canCreateContent(data);
	if (loading || !canCreate) {
		return null;
	}

	return (
		<Tooltip
			content={<FormattedMessage {...i18n.simpleCreateLabel} />}
			position="top"
			hideTooltipOnMouseDown
		>
			<ContextualCreateButton
				{...triggerProps}
				id="contextual-create-content-button"
				appearance="subtle"
				icon={() => (
					<EditorAddIcon
						color={isContextualCreateFocused ? token('color.icon.selected') : token('color.icon')}
						label={intl.formatMessage(i18n.simpleCreateLabel)}
						size="small"
					/>
				)}
				label={
					displayTitle
						? intl.formatMessage(i18n.simpleCreateLabelForTitle, {
								pageTitle: displayTitle,
							})
						: intl.formatMessage(i18n.simpleCreateLabel)
				}
				onClick={handleClick}
				testId="contextual-create-content-button"
				isSelected={isContextualCreateFocused}
				isOpen={isContextualCreateFocused}
				isRootCreateButton={rootCreateButtonStyling}
			/>
		</Tooltip>
	);
};
